import React, { useState, useEffect } from 'react';
import { Helmet } from 'react-helmet';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import QuickActionButton from '../../components/ui/QuickActionButton';
import SleepTimeline from './components/SleepTimeline';
import SleepTrendChart from './components/SleepTrendChart';
import SleepGoalProgress from './components/SleepGoalProgress';
import SleepLoggingControls from './components/SleepLoggingControls';
import SleepInsights from './components/SleepInsights';
import PageTransition from '../../components/PageTransition';
import { useAuth } from '../../contexts/AuthContext';
import { sleepService, activityFeedService } from '../../services/trackingService';
import milestoneService from '../../services/milestoneService';
import MilestonePopup from '../../components/MilestonePopup';
import { useDashboardMetrics } from '../../hooks/useRealtimeMetrics';

const SleepAnalysis = () => {
  const { user } = useAuth();
  const [selectedDate, setSelectedDate] = useState(new Date()?.toISOString()?.split('T')?.[0]);
  const [sleepData, setSleepData] = useState([]);
  const [environmentData, setEnvironmentData] = useState({});
  const [loading, setLoading] = useState(true);
  
  // Milestone state
  const [currentMilestone, setCurrentMilestone] = useState(null);
  const [showMilestonePopup, setShowMilestonePopup] = useState(false);
  
  // Use real-time metrics for milestone detection
  const { metrics } = useDashboardMetrics(user?.id);

  // Check for sleep milestones
  useEffect(() => {
    if (!user?.id || !metrics?.sleep) return;

    const milestone = milestoneService?.checkMilestone(user?.id, 'sleep', metrics?.sleep?.current);
    
    if (milestone) {
      setCurrentMilestone(milestone);
      setShowMilestonePopup(true);
      milestoneService?.createMilestoneNotification(user?.id, milestone);
    }
  }, [metrics?.sleep?.current, user?.id]);

  // Fetch sleep data from Supabase
  useEffect(() => {
    const fetchSleepData = async () => {
      if (!user?.id) return;

      try {
        setLoading(true);
        // Fetch last 7 days of sleep data
        const endDate = new Date()?.toISOString()?.split('T')?.[0];
        const startDate = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000)?.toISOString()?.split('T')?.[0];
        
        const logs = await sleepService?.getSleepLogs(user?.id, startDate, endDate);
        
        // Transform data to match component format
        const transformedData = logs?.map(log => ({
          date: log?.sleep_date,
          bedtime: log?.bedtime,
          wakeTime: log?.wake_time,
          duration: `${log?.duration_hours}h`,
          quality: log?.quality_score,
          consistency: log?.consistency_score,
          phases: [
            { type: 'light', duration: Math.round((log?.light_sleep_hours / log?.duration_hours) * 100) },
            { type: 'deep', duration: Math.round((log?.deep_sleep_hours / log?.duration_hours) * 100) },
            { type: 'rem', duration: Math.round((log?.rem_sleep_hours / log?.duration_hours) * 100) },
            { type: 'awake', duration: 100 - Math.round(((log?.light_sleep_hours + log?.deep_sleep_hours + log?.rem_sleep_hours) / log?.duration_hours) * 100) }
          ]
        }));

        setSleepData(transformedData);
      } catch (error) {
        console.error('Error fetching sleep data:', error);
      } finally {
        setLoading(false);
      }
    };

    fetchSleepData();
  }, [user?.id]);

  const handleLogSleep = async (sleepLogData) => {
    if (!user?.id) return;

    try {
      const newLog = await sleepService?.createSleepLog(user?.id, {
        date: sleepLogData?.date || new Date()?.toISOString()?.split('T')?.[0],
        bedtime: sleepLogData?.bedtime,
        wakeTime: sleepLogData?.wakeTime,
        durationHours: sleepLogData?.durationHours,
        deepSleepHours: sleepLogData?.deepSleepHours || 0,
        lightSleepHours: sleepLogData?.lightSleepHours || 0,
        remSleepHours: sleepLogData?.remSleepHours || 0,
        qualityScore: sleepLogData?.qualityScore || 0,
        consistencyScore: sleepLogData?.consistencyScore || 0,
        notes: sleepLogData?.notes || ''
      });

      // Create activity feed entry
      await activityFeedService?.createActivity(user?.id, {
        type: 'achievement',
        title: 'Sleep Logged',
        description: `${sleepLogData?.durationHours} hours of sleep`
      });

      // Transform and add to state
      const transformedLog = {
        date: newLog?.sleep_date,
        bedtime: newLog?.bedtime,
        wakeTime: newLog?.wake_time,
        duration: `${newLog?.duration_hours}h`,
        quality: newLog?.quality_score,
        consistency: newLog?.consistency_score,
        phases: [
          { type: 'light', duration: Math.round((newLog?.light_sleep_hours / newLog?.duration_hours) * 100) },
          { type: 'deep', duration: Math.round((newLog?.deep_sleep_hours / newLog?.duration_hours) * 100) },
          { type: 'rem', duration: Math.round((newLog?.rem_sleep_hours / newLog?.duration_hours) * 100) },
          { type: 'awake', duration: 100 - Math.round(((newLog?.light_sleep_hours + newLog?.deep_sleep_hours + newLog?.rem_sleep_hours) / newLog?.duration_hours) * 100) }
        ]
      };

      setSleepData(prev => [transformedLog, ...prev]);
    } catch (error) {
      console.error('Error logging sleep:', error);
    }
  };

  const calculateDuration = (bedtime, wakeTime) => {
    const bed = new Date(`2000-01-01T${bedtime}`);
    const wake = new Date(`2000-01-02T${wakeTime}`);
    const duration = (wake - bed) / (1000 * 60 * 60);
    return `${duration?.toFixed(1)}h`;
  };

  const getCurrentSleep = () => {
    const today = sleepData?.find(sleep => sleep?.date === selectedDate);
    return today ? today?.duration : '8.5h';
  };

  const getWeeklyAverage = () => {
    const recentSleep = sleepData?.slice(-7);
    const avgDuration = recentSleep?.reduce((sum, sleep) => 
      sum + parseFloat(sleep?.duration?.replace('h', '')), 0
    ) / recentSleep?.length;
    return `${avgDuration?.toFixed(1)}h`;
  };

  return (
    <>
      <Helmet>
        <title>Sleep Analysis - PrimeOne+</title>
        <meta name="description" content="Comprehensive sleep pattern tracking and analysis for optimal wellness management" />
      </Helmet>
      <div className="min-h-screen bg-background">
        <Header />
        
        <PageTransition>
          <main className="container mx-auto px-4 py-8 max-w-7xl">
            {/* Page Header */}
            <motion.div
              className="mb-8"
              initial={{ opacity: 0, y: -16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, ease: [0.4, 0, 0.2, 1] }}
            >
              <div className="flex items-center gap-4 mb-2">
                <motion.div
                  className="flex items-center justify-center w-12 h-12 bg-primary/10 rounded-xl"
                  whileHover={{ scale: 1.08, rotate: -5 }}
                  transition={{ type: 'spring', stiffness: 300, damping: 20 }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" className="text-primary"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
                </motion.div>
                <h1 className="text-3xl font-bold text-foreground">Sleep Analysis</h1>
              </div>
              <p className="text-muted-foreground">
                Track your sleep patterns, quality, and trends for better wellness management
              </p>
            </motion.div>

            {/* Main Grid Layout */}
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Left Column - Timeline and Trends */}
              <motion.div
                className="lg:col-span-2 space-y-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.1, ease: [0.4, 0, 0.2, 1] }}
              >
                <SleepTimeline 
                  sleepData={sleepData}
                  selectedDate={selectedDate}
                  onDateChange={setSelectedDate}
                />
                <SleepTrendChart sleepData={sleepData} />
                <div className="lg:hidden">
                  <SleepInsights 
                    sleepData={sleepData}
                    environmentData={environmentData}
                  />
                </div>
              </motion.div>

              {/* Right Column */}
              <motion.div
                className="space-y-6"
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.4, delay: 0.2, ease: [0.4, 0, 0.2, 1] }}
              >
                <SleepGoalProgress 
                  currentSleep={getCurrentSleep()}
                  sleepGoal="8.0h"
                  weeklyAverage={getWeeklyAverage()}
                />
                <SleepLoggingControls onLogSleep={handleLogSleep} />
                <div className="hidden lg:block">
                  <SleepInsights 
                    sleepData={sleepData}
                    environmentData={environmentData}
                  />
                </div>
              </motion.div>
            </div>

            {/* Additional Stats Section */}
            <motion.div
              className="mt-8 grid grid-cols-1 md:grid-cols-4 gap-4"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.4, delay: 0.3, ease: [0.4, 0, 0.2, 1] }}
            >
              {[
                { value: '7.2h', label: 'Average Sleep', sub: '+0.3h from last week', subColor: 'text-success' },
                { value: '83%', label: 'Sleep Efficiency', sub: '+5% from last week', subColor: 'text-success' },
                { value: '22:45', label: 'Avg Bedtime', sub: '15min later than goal', subColor: 'text-warning' },
                { value: '6', label: 'Consistent Days', sub: 'Great consistency!', subColor: 'text-success' },
              ]?.map((stat, i) => (
                <motion.div
                  key={stat?.label}
                  className="bg-card rounded-xl p-6 shadow-wellness elevation-1 text-center cursor-default"
                  initial={{ opacity: 0, y: 16 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.35, delay: 0.35 + i * 0.07, ease: [0.4, 0, 0.2, 1] }}
                  whileHover={{ y: -3, boxShadow: '0 10px 25px rgba(0,0,0,0.1)' }}
                >
                  <div className="text-2xl font-bold text-primary mb-2">{stat?.value}</div>
                  <div className="text-sm text-muted-foreground">{stat?.label}</div>
                  <div className={`text-xs mt-1 ${stat?.subColor}`}>{stat?.sub}</div>
                </motion.div>
              ))}
            </motion.div>
          </main>
        </PageTransition>

        <QuickActionButton />
        
        <MilestonePopup
          milestone={currentMilestone}
          isOpen={showMilestonePopup}
          onClose={() => setShowMilestonePopup(false)}
        />
      </div>
    </>
  );
};

export default SleepAnalysis;